import { FormTypes } from '@oneblink/types'

import {
  findElement,
  replaceElementOptions,
  createElementOptions,
} from '../../definition-manipulation'
import makeIsShowing from '../../is-element-showing'
import {
  findPerson,
  generateName,
  getContactEmails,
  getContactNames,
  getContactPhones,
  getPeople,
  getOtherPerson,
  PeopleLike,
  setToSortedArray,
} from '../common/contacts'

function getAllNonFixedPremisePeople(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
): Array<Partial<PeopleLike>> {
  const avokaPeople: Array<Partial<PeopleLike>> = getPeople(
    submission,
    definition.elements,
  )

  const emergencyContact = getOtherPerson(
    {
      First_Name: 'Emergency_First_Name',
      Last_Name: 'Emergency_Last_Name',
    },
    submission,
    definition.elements,
  )

  if (emergencyContact) {
    avokaPeople.push({
      ...emergencyContact,
      Phone: submission.Emergency_Mobile as string,
    })
  }

  return avokaPeople
}

function getFixedPremisePeople(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
): Array<Partial<PeopleLike>> {
  const fixedPremisePeople: Array<Partial<PeopleLike>> = []

  const fixedPremiseSubmissions =
    (submission.fixedPremises as Array<{
      nswFoodLicenceApplicationFormFixedPremises: {
        First_Name?: string
        Last_Name?: string
      }
    }>) || []

  const fixedPremiseForm = findElement<FormTypes.FormFormElement>(
    definition,
    'nswFoodLicenceApplicationFormFixedPremises',
  )

  if (
    fixedPremiseForm &&
    fixedPremiseForm.value &&
    fixedPremiseForm.value.elements
  ) {
    fixedPremiseSubmissions.forEach((sub) => {
      const otherContactName = getOtherPerson(
        {
          Title: 'Title',
          First_Name: 'First_Name',
          Last_Name: 'Last_Name',
          Phone: 'Phone',
          Email: 'Email',
        },
        sub.nswFoodLicenceApplicationFormFixedPremises,
        fixedPremiseForm.value.elements || [],
      )

      if (otherContactName) {
        fixedPremisePeople.push(otherContactName)
      }
    })
  }
  return fixedPremisePeople
}

export function populateContactElements(
  liveSubmission: Record<string, unknown>,
  definition: FormTypes.Form,
) {
  const contactNames = getContactNames(liveSubmission, definition.elements)

  const nonFixedPremisePeople = getAllNonFixedPremisePeople(
    liveSubmission,
    definition,
  )

  const avokaContactNames = nonFixedPremisePeople.map(generateName)

  const avokaContacts = new Set(avokaContactNames)

  const fixedPremisePeople = getFixedPremisePeople(liveSubmission, definition)

  const fixedPremiseContactNames = fixedPremisePeople.map(generateName)

  const fixedPremiseContacts = new Set(fixedPremiseContactNames)

  replaceElementOptions(
    definition,
    'Emergency_Contact',
    createElementOptions(setToSortedArray(contactNames)),
  )

  replaceElementOptions(
    definition,
    'Avoka_contact_reference',
    createElementOptions(setToSortedArray(avokaContacts)),
    ['New contact'],
  )

  const mergedContacts = new Set([...avokaContacts, ...fixedPremiseContacts])

  replaceElementOptions(
    definition,
    'personCompletingThisFormAndProvidingCertificationS',
    createElementOptions(setToSortedArray(mergedContacts)),
  )

  replaceElementOptions(
    definition,
    'Main_Email',
    createElementOptions(
      setToSortedArray(getContactEmails(liveSubmission, definition.elements)),
    ),
  )

  replaceElementOptions(
    definition,
    'Main_Phone',
    createElementOptions(
      setToSortedArray(getContactPhones(liveSubmission, definition.elements)),
    ),
  )
  return definition
}

export function personCompletingFormDetails(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
) {
  const personSelected =
    submission.personCompletingThisFormAndProvidingCertificationS

  if (typeof personSelected === 'string' && personSelected !== 'Other') {
    const people = getAllNonFixedPremisePeople(submission, definition)
    people.push(...getFixedPremisePeople(submission, definition))
    const personCompleting = findPerson(personSelected, people)

    submission.Title_Authorised__hidden = personCompleting?.Title
    submission.First_Name_Authorised__hidden = personCompleting?.First_Name
    submission.Last_name_Authorised__hidden = personCompleting?.Last_Name
    submission.Phone_Authorised__hidden = personCompleting?.Phone
    submission.Email_Authorised__hidden =
      personCompleting?.Email !== 'Other'
        ? personCompleting?.Email
        : personCompleting?.Other_email
  } else {
    submission.Title_Authorised__hidden = submission.Title_Authorised
    submission.First_Name_Authorised__hidden = submission.First_Name_Authorised
    submission.Last_name_Authorised__hidden = submission.Last_name_Authorised
    submission.Phone_Authorised__hidden = submission.Phone_Authorised
    submission.Email_Authorised__hidden = submission.Email_Authorised
  }
}

export function avokaContactReferenceDetails(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
) {
  const fixedPremises = submission.fixedPremises
  if (Array.isArray(fixedPremises)) {
    //contacts that were added elsewhere in the form
    const people = getAllNonFixedPremisePeople(submission, definition)
    if (Array.isArray(people) && people.length) {
      fixedPremises.forEach((fp, index) => {
        const personSelected =
          fp?.nswFoodLicenceApplicationFormFixedPremises
            ?.Avoka_contact_reference
        if (personSelected !== 'New contact') {
          const person = findPerson(personSelected, people)
          if (person) {
            fp.nswFoodLicenceApplicationFormFixedPremises.Title__hidden =
              person?.Title
            fp.nswFoodLicenceApplicationFormFixedPremises.First_Name__hidden =
              person?.First_Name
            fp.nswFoodLicenceApplicationFormFixedPremises.Last_Name__hidden =
              person?.Last_Name
            fp.nswFoodLicenceApplicationFormFixedPremises.Phone__hidden =
              person?.Phone
            fp.nswFoodLicenceApplicationFormFixedPremises.Email__hidden =
              person?.Email !== 'Other' ? person?.Email : person.Other_email
            fp.nswFoodLicenceApplicationFormFixedPremises.Job_Function__hidden =
              person?.Job_Function
          } else {
            console.warn(
              `Could not find ${personSelected}'s details in current form`,
            )
          }
        } else if (fp.nswFoodLicenceApplicationFormFixedPremises) {
          fp.nswFoodLicenceApplicationFormFixedPremises.Title__hidden =
            fp.nswFoodLicenceApplicationFormFixedPremises.Title
          fp.nswFoodLicenceApplicationFormFixedPremises.First_Name__hidden =
            fp.nswFoodLicenceApplicationFormFixedPremises.First_Name
          fp.nswFoodLicenceApplicationFormFixedPremises.Last_Name__hidden =
            fp.nswFoodLicenceApplicationFormFixedPremises.Last_Name
          fp.nswFoodLicenceApplicationFormFixedPremises.Phone__hidden =
            fp.nswFoodLicenceApplicationFormFixedPremises.Phone
          fp.nswFoodLicenceApplicationFormFixedPremises.Email__hidden =
            fp.nswFoodLicenceApplicationFormFixedPremises.Email
          fp.nswFoodLicenceApplicationFormFixedPremises.Job_Function__hidden =
            fp.nswFoodLicenceApplicationFormFixedPremises.Job_Function
        }
      })
    }
  }
}

export function emergencyContact(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
) {
  const personSelected = submission?.Emergency_Contact
  const isShowing = makeIsShowing(submission, definition.elements)

  if (typeof personSelected === 'string' && personSelected !== 'Other') {
    const people = getPeople(submission, definition.elements)
    const person = findPerson(personSelected, people)
    submission.Emergency_First_Name__hidden = person?.First_Name
    submission.Emergency_Last_Name__hidden = person?.Last_Name
    submission.Emergency_Mobile__hidden =
      submission.emergencyContactNumberIsMobile__hidden
        ? person?.Phone
        : submission.Emergency_Mobile
  } else {
    submission.Emergency_First_Name__hidden = submission.Emergency_First_Name
    submission.Emergency_Last_Name__hidden = submission.Emergency_Last_Name
    if (isShowing('Emergency_Mobile')) {
      submission.Emergency_Mobile__hidden = submission.Emergency_Mobile
    }
  }
}

export function updateEmergencyContactIsMobile(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
) {
  let emergencyContactNumberIsMobile = false
  const selectedEmergencyContactName = submission.Emergency_Contact
  if (
    selectedEmergencyContactName &&
    typeof selectedEmergencyContactName === 'string'
  ) {
    const people = getPeople(submission, definition.elements)
    const emergencyContact = findPerson(selectedEmergencyContactName, people)
    if (emergencyContact && emergencyContact.Phone) {
      emergencyContactNumberIsMobile = emergencyContact.Phone.startsWith('04')
    }
  }

  submission.emergencyContactNumberIsMobile__hidden =
    emergencyContactNumberIsMobile
}
